import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUniversity, FaWallet, FaBuilding, FaHands, 
  FaUsers, FaShieldAlt, FaLaptop, FaMicrochip,
  FaHospital, FaSchool, FaIndustry, FaChartLine,
  FaTruck, FaHotel, FaStore, FaGlobe, FaTree,
  FaArrowLeft, FaArrowRight, FaCircle
} from 'react-icons/fa';

const industries = [
  { 
    name: 'SACCOs', 
    icon: FaUniversity, 
    color: '#E8960D', 
    bg: 'bg-amber-500/10',
    description: 'Savings & Credit Cooperatives',
    gradient: 'from-amber-500 to-orange-500'
  },
  { 
    name: 'Microfinance', 
    icon: FaWallet, 
    color: '#0a7e3a', 
    bg: 'bg-emerald-500/10',
    description: 'Financial Inclusion',
    gradient: 'from-emerald-500 to-teal-500'
  },
  { 
    name: 'Banks', 
    icon: FaBuilding, 
    color: '#074578', 
    bg: 'bg-blue-500/10',
    description: 'Commercial & Retail Banking',
    gradient: 'from-blue-500 to-indigo-500'
  },
  { 
    name: 'NGOs', 
    icon: FaHands, 
    color: '#E8960D', 
    bg: 'bg-amber-500/10',
    description: 'Non-Profit Organizations',
    gradient: 'from-amber-500 to-yellow-500'
  },
  { 
    name: 'Government', 
    icon: FaUniversity, 
    color: '#074578', 
    bg: 'bg-blue-500/10',
    description: 'Public Sector',
    gradient: 'from-blue-500 to-cyan-500'
  },
  { 
    name: 'Cooperatives', 
    icon: FaUsers, 
    color: '#0a7e3a', 
    bg: 'bg-emerald-500/10',
    description: 'Member-Owned Organizations',
    gradient: 'from-emerald-500 to-green-500'
  },
  { 
    name: 'Insurance', 
    icon: FaShieldAlt, 
    color: '#E8960D', 
    bg: 'bg-amber-500/10',
    description: 'Insurance & Risk Management',
    gradient: 'from-amber-500 to-orange-500'
  },
  { 
    name: 'SMEs', 
    icon: FaIndustry, 
    color: '#074578', 
    bg: 'bg-blue-500/10',
    description: 'Small & Medium Enterprises',
    gradient: 'from-blue-500 to-purple-500'
  },
  { 
    name: 'Schools', 
    icon: FaSchool, 
    color: '#0a7e3a', 
    bg: 'bg-emerald-500/10',
    description: 'Educational Institutions',
    gradient: 'from-emerald-500 to-teal-500'
  },
  { 
    name: 'Hospitals', 
    icon: FaHospital, 
    color: '#E8960D', 
    bg: 'bg-amber-500/10',
    description: 'Healthcare & Medical',
    gradient: 'from-red-500 to-rose-500'
  },
  { 
    name: 'Agriculture', 
    icon: FaTree, 
    color: '#0a7e3a', 
    bg: 'bg-emerald-500/10',
    description: 'Agribusiness & Farming',
    gradient: 'from-green-500 to-emerald-500'
  },
  { 
    name: 'Manufacturing', 
    icon: FaIndustry, 
    color: '#074578', 
    bg: 'bg-blue-500/10',
    description: 'Production & Manufacturing',
    gradient: 'from-blue-500 to-cyan-500'
  },
  { 
    name: 'Retail', 
    icon: FaStore, 
    color: '#E8960D', 
    bg: 'bg-amber-500/10',
    description: 'Retail & E-commerce',
    gradient: 'from-amber-500 to-orange-500'
  },
  { 
    name: 'Logistics', 
    icon: FaTruck, 
    color: '#074578', 
    bg: 'bg-blue-500/10',
    description: 'Transport & Logistics',
    gradient: 'from-blue-500 to-indigo-500'
  },
  { 
    name: 'Hospitality', 
    icon: FaHotel, 
    color: '#0a7e3a', 
    bg: 'bg-emerald-500/10',
    description: 'Hotels & Tourism',
    gradient: 'from-emerald-500 to-teal-500'
  },
];

export default function Industries() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerView, setItemsPerView] = useState(5);

  // Handle responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 480) {
        setItemsPerView(2);
      } else if (window.innerWidth < 640) {
        setItemsPerView(3);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(4);
      } else {
        setItemsPerView(5);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex + itemsPerView >= industries.length ? 0 : prevIndex + itemsPerView
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, itemsPerView]);

  const totalSlides = Math.ceil(industries.length / itemsPerView);
  const maxIndex = Math.max(0, totalSlides - 1);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) => 
      prevIndex + itemsPerView >= industries.length ? 0 : prevIndex + itemsPerView
    );
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) => 
      prevIndex - itemsPerView < 0 ? Math.max(0, industries.length - itemsPerView) : prevIndex - itemsPerView
    );
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index * itemsPerView);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const getVisibleIndustries = () => {
    return industries.slice(currentIndex, currentIndex + itemsPerView);
  };

  return (
    <section className="section bg-gray-50 scroll-mt-20">
      <div className="container-custom">
        <div className="text-center mb-10 md:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-micropoint-gold/10 text-micropoint-gold px-4 py-1 rounded-full text-sm font-semibold mb-3">
              Our Expertise
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-display">
              Industries <span className="text-micropoint-gold">We Serve</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-micropoint-gold to-yellow-500 mx-auto mt-4 rounded-full" />
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-sm md:text-base">
              Trusted solutions delivered across diverse sectors with deep industry expertise
            </p>
          </motion.div>
        </div>

        <div className="relative">
          {/* Carousel Container */}
          <div className="overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4"
              >
                {getVisibleIndustries().map((industry, index) => {
                  const Icon = industry.icon;
                  return (
                    <motion.div
                      key={industry.name}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="group relative bg-white rounded-2xl p-4 md:p-5 text-center border border-gray-200 hover:border-micropoint-gold hover:shadow-xl transition-all duration-300 cursor-default overflow-hidden"
                    >
                      {/* Top gradient bar */}
                      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${industry.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                      
                      <div
                        className={`w-12 h-12 md:w-14 md:h-14 mx-auto rounded-2xl ${industry.bg} flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3`}
                      >
                        <Icon className="text-xl md:text-2xl" style={{ color: industry.color }} />
                      </div>
                      <p className="text-xs md:text-sm font-semibold text-gray-800 group-hover:text-micropoint-gold transition-colors duration-300">
                        {industry.name}
                      </p>
                      <p className="text-[8px] md:text-[10px] text-gray-400 mt-0.5">
                        {industry.description}
                      </p>
                      <div className="w-8 h-0.5 bg-micropoint-gold/30 mx-auto mt-1.5 rounded-full group-hover:w-12 transition-all duration-300" />
                      
                      {/* Hover arrow indicator */}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity mt-2">
                        <FaArrowRight className="text-micropoint-gold/50 text-[8px] mx-auto" />
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          {industries.length > itemsPerView && (
            <>
              <button
                onClick={prevSlide}
                className="absolute -left-3 md:-left-4 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm hover:bg-white text-gray-700 hover:text-micropoint-gold w-8 h-8 md:w-10 md:h-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-micropoint-gold flex items-center justify-center z-10"
                aria-label="Previous"
              >
                <FaArrowLeft className="text-xs md:text-sm" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute -right-3 md:-right-4 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm hover:bg-white text-gray-700 hover:text-micropoint-gold w-8 h-8 md:w-10 md:h-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-micropoint-gold flex items-center justify-center z-10"
                aria-label="Next"
              >
                <FaArrowRight className="text-xs md:text-sm" />
              </button>
            </>
          )}
        </div>

        {/* Dot indicators */}
        {totalSlides > 1 && (
          <div className="flex justify-center gap-2 mt-6 md:mt-8">
            {Array.from({ length: totalSlides }).map((_, index) => {
              const isActive = Math.floor(currentIndex / itemsPerView) === index;
              return (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    isActive 
                      ? 'w-8 h-2.5 bg-micropoint-gold shadow-lg shadow-micropoint-gold/20' 
                      : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              );
            })}
          </div>
        )}

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-gray-200"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { number: '15+', label: 'Industries Served', color: 'from-amber-500 to-orange-500' },
              { number: '50+', label: 'Implementations', color: 'from-blue-500 to-indigo-500' },
              { number: '15+', label: 'Years Experience', color: 'from-emerald-500 to-teal-500' },
              { number: '98%', label: 'Client Satisfaction', color: 'from-purple-500 to-pink-500' },
            ].map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-4 text-center border border-gray-200 hover:border-micropoint-gold transition-all hover:shadow-lg group">
                <div className={`text-xl md:text-2xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {stat.number}
                </div>
                <div className="text-xs md:text-sm text-gray-500 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}